import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, X } from 'lucide-react';

const companySchema = z.object({
  name: z.string().min(1, 'Company name is required').max(200),
  category: z.enum(['services', 'staffing', 'end_client', 'unknown', 'recruiter', 'it_company', 'staff_augmentor', 'consulting']),
  crm_status: z.enum(['lead', 'prospect', 'client', 'on_hold', 'lost']),
  location: z.string().max(200).optional(),
  industry: z.string().max(100).optional(),
  description: z.string().max(1000).optional(),
  website_url: z.string().url('Invalid URL').optional().or(z.literal('')),
  linkedin_url: z.string().url('Invalid URL').optional().or(z.literal('')),
  employee_count_estimate: z.coerce.number().int().positive().optional().or(z.literal('')),
  primary_location: z.string().max(200).optional(),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyFormProps {
  company?: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  services: 'Services',
  staffing: 'Staffing',
  end_client: 'End Client',
  unknown: 'Unknown',
  recruiter: 'Recruiter',
  it_company: 'IT Company',
  staff_augmentor: 'Staff Augmentor',
  consulting: 'Consulting',
};

const CompanyForm = ({ company, open, onOpenChange, onSuccess }: CompanyFormProps) => {
  const { toast } = useToast();
  const [secondaryLocations, setSecondaryLocations] = useState<string[]>([]);
  const [newLocation, setNewLocation] = useState('');

  const form = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      category: 'unknown',
      crm_status: 'lead',
      location: '',
      industry: '',
      description: '',
      website_url: '',
      linkedin_url: '',
      employee_count_estimate: undefined,
      primary_location: '',
    },
  });

  useEffect(() => {
    if (company) {
      form.reset({
        name: company.name || '',
        category: company.category || 'unknown',
        crm_status: company.crm_status || 'lead',
        location: company.location || '',
        industry: company.industry || '',
        description: company.description || '',
        website_url: company.website_url || '',
        linkedin_url: company.linkedin_url || '',
        employee_count_estimate: company.employee_count_estimate || undefined,
        primary_location: company.primary_location || '',
      });
      setSecondaryLocations(company.secondary_locations || []);
    } else {
      form.reset();
      setSecondaryLocations([]);
    }
  }, [company, form]);

  const addSecondaryLocation = () => {
    if (newLocation.trim() && !secondaryLocations.includes(newLocation.trim())) {
      setSecondaryLocations([...secondaryLocations, newLocation.trim()]);
      setNewLocation('');
    }
  };

  const removeSecondaryLocation = (loc: string) => {
    setSecondaryLocations(secondaryLocations.filter(l => l !== loc));
  };

  const onSubmit = async (data: CompanyFormData) => {
    try {
      const payload = {
        ...data,
        employee_count_estimate: data.employee_count_estimate || null,
        secondary_locations: secondaryLocations,
      };

      if (company) {
        const { error } = await supabase
          .from('companies')
          .update(payload)
          .eq('id', company.id);
        if (error) throw error;
        toast({ title: 'Company updated successfully' });
      } else {
        const { error } = await supabase.from('companies').insert([payload]);
        if (error) throw error;
        toast({ title: 'Company created successfully' });
      }
      onSuccess();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{company ? 'Edit Company' : 'Add New Company'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Technology, Healthcare" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="crm_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CRM Status *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="lead">Lead</SelectItem>
                        <SelectItem value="prospect">Prospect</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="primary_location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Primary Location (Headquarters)</FormLabel>
                  <FormControl>
                    <Input placeholder="City, Country" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <FormLabel>Secondary Locations</FormLabel>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Add another location"
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSecondaryLocation())}
                />
                <Button type="button" variant="outline" onClick={addSecondaryLocation}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {secondaryLocations.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {secondaryLocations.map((loc, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                      {loc}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => removeSecondaryLocation(loc)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Legacy Location (for display)</FormLabel>
                  <FormControl>
                    <Input placeholder="City, Country" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="employee_count_estimate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee Count</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="website_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="linkedin_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://linkedin.com/company/..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {company ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyForm;
